import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Modal asking the user to choose a Lost Reason before a deal becomes Lost.
 *
 * Props:
 * - dealName: string — shown in the headline.
 * - existingReasonId, existingNotes: prefill (used when editing the reason on an already-lost deal).
 * - onCancel(): user backed out — caller should revert any optimistic state.
 * - onConfirm({ reasonId, notes }): user picked a reason — caller submits to backend.
 */
export default function LostReasonModal({
  dealName,
  existingReasonId,
  existingNotes,
  onCancel,
  onConfirm,
}) {
  const [reasons, setReasons] = useState([]);
  const [selected, setSelected] = useState(existingReasonId || "");
  const [notes, setNotes] = useState(existingNotes || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/lost-reasons")
      .then((r) => setReasons(r.data))
      .catch(() => toast.error("Couldn't load lost reasons"))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (!selected) {
      toast.error("Pick a reason first");
      return;
    }
    setSaving(true);
    await onConfirm({ reasonId: selected, notes });
    setSaving(false);
  };

  const selectedReason = reasons.find((r) => r.id === selected);

  return (
    <>
      <div className="side-panel-backdrop" onClick={saving ? undefined : onCancel} />
      <div
        className="pg-card"
        data-testid="lost-reason-modal"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: "1.75rem 2rem",
          width: "min(560px, 92vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          zIndex: 60,
          borderColor: "rgba(239, 68, 68, 0.30)",
        }}
      >
        <div className="mb-1">
          <span
            className="pg-badge"
            style={{
              background: "rgba(239, 68, 68, 0.10)",
              color: "var(--pg-red)",
              border: "1px solid rgba(239, 68, 68, 0.30)",
            }}
          >
            MARKING AS LOST
          </span>
        </div>
        <h2 className="text-xl mt-3 mb-2" style={{ fontWeight: 600 }}>
          Why was this deal lost?
        </h2>
        <p className="text-sm text-dim mb-5">
          Deal: <strong style={{ color: "var(--pg-text)" }}>{dealName}</strong>
        </p>

        {loading ? (
          <div className="mono text-xs text-mute py-4">loading reasons…</div>
        ) : reasons.length === 0 ? (
          <div className="mono text-xs text-mute py-4">
            No Lost Reasons configured. Ask an admin to set them up in Settings → Lost Reasons.
          </div>
        ) : (
          <div className="space-y-2 mb-5">
            {reasons.map((r) => {
              const isSel = r.id === selected;
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setSelected(r.id)}
                  className="block w-full text-left pg-card p-3"
                  data-testid={`lost-reason-option-${r.id}`}
                  style={{
                    background: isSel ? "rgba(110, 240, 220, 0.06)" : "var(--pg-surface-2)",
                    borderColor: isSel ? "var(--pg-accent)" : "var(--pg-border)",
                    cursor: "pointer",
                    transition: "background 140ms ease, border-color 140ms ease",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div style={{ color: "var(--pg-text)", fontWeight: 600 }}>{r.title}</div>
                      {r.description && (
                        <div className="mono text-[11px] text-mute mt-0.5" style={{ lineHeight: 1.5 }}>
                          {r.description}
                        </div>
                      )}
                    </div>
                    {isSel && (
                      <span
                        aria-hidden
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: "var(--pg-accent)",
                        }}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
          Additional Context (optional)
        </label>
        <textarea
          className="pg-input"
          rows={3}
          placeholder={
            selectedReason ? `Anything else about why "${selectedReason.title}"?` : "Optional notes…"
          }
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="lost-reason-notes"
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            className="pg-btn"
            onClick={onCancel}
            disabled={saving}
            data-testid="lost-reason-cancel"
          >
            Never mind
          </button>
          <button
            type="button"
            className="pg-btn pg-btn-danger"
            onClick={submit}
            disabled={saving || !selected}
            data-testid="lost-reason-confirm"
          >
            {saving ? "Marking…" : "Mark Lost"}
          </button>
        </div>
      </div>
    </>
  );
}
